import { AVERAGE_VIDEO_GENERATION_IN_MILLISECONDS, VIDEO_WITH_WATERMARK_SUFFIX } from '@/constants/video';
import { StudioVideo } from '@/features/ai-video-creator/stores/studioVideoStore';
import { vodAsset } from '@/types/entities';
import { v4 as uuidv4 } from 'uuid';
import { useUserDataStore } from '@/stores/userDataStore';
import { MediaAsset, MediaAssetType } from '@/types/media';
export const getVodAssetUrl = (vodAsset: vodAsset | undefined): string => {
  if (!vodAsset) {
    return '';
  }
  let fileName = `${vodAsset.id}_1920.webp`;
  if (vodAsset.type === 'video') {
    fileName = `${vodAsset.id}${VIDEO_WITH_WATERMARK_SUFFIX}.mp4`;
  }

  return `${process.env.NEXT_PUBLIC_TOLSTOY_VIDEO_BASE_URL}/public/${vodAsset.owner}/${vodAsset.id}/${fileName}`;
};

export const getVideoPath = ({
  video,
  shouldRemoveWatermark = false,
  s3Bucket = 'output',
}: {
  video: StudioVideo;
  shouldRemoveWatermark?: boolean;
  s3Bucket?: 'input' | 'output';
}): string => {
  const owner = useUserDataStore.getState().owner;
  const id = video.vodAsset?.id || video.id;

  if (s3Bucket === 'input') {
    return `public/${owner}/${id}.webm`;
  }

  if (shouldRemoveWatermark || !video.vodAsset) {
    return `public/${owner}/${id}/${id}.mp4`;
  }
  return `public/${owner}/${id}/${id}${VIDEO_WITH_WATERMARK_SUFFIX}.mp4`;
};

export const getVideoUrl = ({
  video,
  shouldRemoveWatermark = false,
}: {
  video: StudioVideo;
  shouldRemoveWatermark?: boolean;
}): string => {
  if (!video) {
    return '';
  }

  return `${process.env.NEXT_PUBLIC_TOLSTOY_VIDEO_BASE_URL}/${getVideoPath({ video, shouldRemoveWatermark })}`;
};

export const getVideoPosterUrl = (video: StudioVideo, shouldRemoveWatermark?: boolean) => {
  if (!video) {
    return '';
  }
  const id = video.vodAsset?.id || video.id;
  const owner = useUserDataStore.getState().owner;

  return `${process.env.NEXT_PUBLIC_TOLSTOY_VIDEO_BASE_URL}/public/${owner}/${id}/${id}.401.0000000.webp`;
};

export const download = async (fileUrl: string, fileName: string): Promise<void> => {
  const file = await fetch(fileUrl);
  const blob = await file.blob();

  const fileExtension = blob.type.split('/')[1];
  const fileNameWithExtension = fileName.endsWith(`.${fileExtension}`) ? fileName : `${fileName}.${fileExtension}`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fileNameWithExtension;
  a.click();
};

const getOwnerPath = ({ owner }) => {
  return `${process.env.NEXT_PUBLIC_TOLSTOY_VIDEO_BASE_URL}/public/${owner}`;
};

export const estimateVideoGenerationProgress = (createdAtTime: number) => {
  const now = Date.now();
  const elapsedTime = now - createdAtTime;

  return Math.min((elapsedTime / AVERAGE_VIDEO_GENERATION_IN_MILLISECONDS) * 100, 100);
};

export const getImageRenditionUrl = (video, { size = 960 } = {}) => {
  if (video.stockAsset?.posterUrl) {
    return video.stockAsset.posterUrl;
  }

  const route = `${getOwnerPath(video)}/${video.id}/`;
  const fileName = `${video.id}_480.webp`;

  return `${route}${fileName}`;
};

export const getIsAiVideo = (video: vodAsset) => {
  return video?.uploadType?.includes('aiVideo');
};

export const urlToFile = async (url: string): Promise<File> => {
  const response = await fetch(url);
  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = '';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  if (!filename) {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const lastSegment = pathname.split('/').pop() || '';
    filename = lastSegment.split('?')[0];
  }

  if (!filename) {
    const extension = blob.type.split('/').pop() || '';
    filename = `${uuidv4()}.${extension}`;
  }

  return new File([blob], filename, { type: blob.type });
};

export const convertStudioVideoToMediaAsset = (video: StudioVideo): MediaAsset => {
  const videoUrl = getVideoUrl({ video: video! });
  return {
    ...video,
    id: video.id || '',
    name: video.name || '',
    src: videoUrl || '',
    type: MediaAssetType.video,
  };
};
